import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { useSession } from "@/lib/auth-client";
import { pageTab } from "@/lib/telemetry-events";
import { readInstallGuess, readStandalone } from "@/lib/install-platform";
import { DEFAULT_APP_ICON, isAppIconKey } from "@/lib/app-icon";
import {
  INSTALL_SHOWN_AFTER_MS,
  dismissInstallBar,
  installBarKind,
  logInstallBarShown,
  logInstallEvent,
  promptInstall,
  useInstallAllowedHere,
  useInstallPrompt,
} from "@/lib/install-prompt";

/**
 * #4974 INSTALLPROMPT: one line under the app bar telling a signed-in person
 * they can install crosbe.ai, on the devices where that works. The rules are
 * installBarKind's (lib/install-prompt.ts). The tile is the icon they chose in
 * Settings, the one the install puts on their taskbar or home screen.
 *
 * `ready` is AppShell's: false until the shell's first effects have run. An
 * unattended page registers in an effect after it paints, and this bar
 * renders before the page, so without the wait it would paint on such a page
 * for one frame. The live board's routes are also denied by path during
 * render (useInstallAllowedHere), since its chunk loads after the shell.
 *
 * install_bar_shown waits until the bar has stayed on one page for
 * INSTALL_SHOWN_AFTER_MS, so a redirect from / has run and the row carries
 * the page the bar is shown on. An answer sooner logs it first, so every
 * answer has its impression.
 */
const COPY = {
  prompt: "Install Crosbe AI as an app.",
  steps: "Add Crosbe AI to your home screen.",
} as const;

// 44 px tap targets below lg, the app bar's density at lg and up (#4334).
const ACTION =
  "inline-flex h-11 shrink-0 items-center rounded-md px-2.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:h-8 lg:px-3";
const PRIMARY = `${ACTION} bg-primary text-primary-foreground hover:bg-primary/90`;
const QUIET = `${ACTION} text-muted-foreground hover:bg-muted hover:text-foreground`;

export function InstallBar({ iconKey, ready }: { iconKey?: string; ready: boolean }) {
  const { data: session } = useSession();
  const allowed = useInstallAllowedHere();
  const prompt = useInstallPrompt();
  const { pathname } = useLocation();
  // Neither changes without a reload.
  const [device] = useState(() => ({
    standalone: readStandalone(),
    platform: readInstallGuess().platform,
  }));
  const kind = installBarKind({
    signedIn: !!session?.user,
    allowed: ready && allowed === true,
    ...device,
    prompt,
    now: Date.now(),
  });

  useEffect(() => {
    if (!kind) return;
    const id = setTimeout(() => logInstallBarShown(kind, pageTab(pathname)), INSTALL_SHOWN_AFTER_MS);
    return () => clearTimeout(id);
  }, [kind, pathname]);

  if (!kind) return null;
  const icon = `/icons/icon-${isAppIconKey(iconKey) ? iconKey : DEFAULT_APP_ICON}-180.png`;
  const seen = () => logInstallBarShown(kind, pageTab(pathname));

  return (
    <section
      aria-label="Install Crosbe AI"
      data-testid="install-bar"
      className="install-bar flex shrink-0 items-center gap-2.5 border-b border-border bg-card px-4 py-1.5 text-card-foreground lg:gap-4 lg:px-6 print:hidden"
    >
      <img src={icon} alt="" width={28} height={28} className="h-6 w-6 shrink-0 rounded-[22.5%] shadow-sm ring-1 ring-black/10 lg:h-7 lg:w-7" />
      <p className="min-w-0 flex-1 text-balance text-sm leading-snug lg:flex-none">{COPY[kind]}</p>
      <div className="flex shrink-0 items-center gap-1">
        {kind === "prompt" ? (
          <button
            type="button"
            onClick={() => {
              seen();
              void promptInstall("bar");
            }}
            className={PRIMARY}
          >
            Install
          </button>
        ) : (
          <Link
            to="/install"
            onClick={() => {
              seen();
              logInstallEvent("install_accepted", { from: "bar", via: "steps" });
            }}
            className={PRIMARY}
          >
            Show me how
          </Link>
        )}
        <button
          type="button"
          onClick={() => {
            seen();
            dismissInstallBar();
          }}
          className={QUIET}
        >
          Not now
        </button>
      </div>
    </section>
  );
}
